import React, { useEffect, useState, useCallback } from 'react';
import {
  LogOut, Search, Download, RefreshCw, Users, UserPlus, Clock, CheckCircle2,
  Filter, X, ChevronLeft, ChevronRight, XCircle, PhoneCall, CalendarDays, Settings,
  PhoneOff, Ban, Database, UserCheck, Trash2, ShieldX, ChevronDown, ChevronUp,
  Construction, Flag, AlertTriangle,
} from 'lucide-react';
import { signOut, getCurrentAdmin } from './auth';
import {
  fetchAllLeads, fetchDashboardStats, updateLeadStatus, startPolling, exportLeadsToCSV,
  exportLeadsFromServer, fetchAdminSiteSettings, setMaintenanceMode,
} from './api';
import type { AdminUser, UnifiedLead, DashboardStats, LeadFilters, LeadStatus } from './types';
import {
  LEAD_STATUSES, STATUS_COLORS, PRIMARY_STATUSES, FLAGGED_STATUSES, isFlaggedLead,
} from './types';

type FlaggedSectionFilter = 'All' | LeadStatus | 'Anti-spam';
import LeadRow from './LeadRow';
import LeadDetailModal from './LeadDetailModal';
import UserManagement from './UserManagement';

const ITEMS_PER_PAGE = 15;

const DEFAULT_FILTERS: LeadFilters = {
  search: '',
  status: 'All',
  sourcePage: '',
  dateFrom: '',
  dateTo: '',
};

// --- Status Change Modal ---
function StatusChangeModal({ lead, onConfirm, onCancel }: {
  lead: UnifiedLead;
  onConfirm: (status: LeadStatus, remark: string, calledDate: string, uid: string) => void;
  onCancel: () => void;
}) {
  const [status, setStatus] = useState<LeadStatus>(lead.status);
  const [remark, setRemark] = useState(lead.remark || '');
  const [uid, setUid] = useState(lead.uid || '');
  const [calledDate, setCalledDate] = useState(
    lead.called_date ? lead.called_date.split('T')[0] : new Date().toISOString().split('T')[0]
  );

  const showUidField = status === 'Added in CRM' || status === 'Already Existing';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-slate-900">Update Status</h3>
            <p className="text-xs text-slate-500 mt-0.5">{lead.name} — {lead.email}</p>
          </div>
          <button onClick={onCancel} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {/* Status Selection */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Status</label>
            <div className="flex flex-wrap gap-2">
              {LEAD_STATUSES.map((s) => {
                const colors = STATUS_COLORS[s];
                const isActive = status === s;
                return (
                  <button
                    key={s}
                    onClick={() => setStatus(s)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                      isActive
                        ? `${colors.bg} ${colors.text} border-current ring-2 ring-offset-1 ring-current/20`
                        : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${isActive ? colors.dot : 'bg-slate-300'}`} />
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Called Date */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              <CalendarDays className="w-3 h-3 inline mr-1" />Called Date
            </label>
            <input
              type="date"
              value={calledDate}
              onChange={(e) => setCalledDate(e.target.value)}
              onClick={(e) => { try { (e.currentTarget as any).showPicker(); } catch {} }}
              autoComplete="off"
              data-form-type="other"
              className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all cursor-pointer"
            />
          </div>

          {/* UID Field — only for Added in CRM / Already Existing */}
          {showUidField && (
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                UID
                <span className="ml-1.5 text-[10px] font-bold text-indigo-500 normal-case tracking-normal">— Required</span>
              </label>
              <input
                type="text"
                value={uid}
                onChange={(e) => setUid(e.target.value)}
                placeholder="Enter UID"
                autoComplete="off"
                className="w-full h-10 px-3 rounded-xl border border-indigo-300 bg-indigo-50/30 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all"
              />
            </div>
          )}

          {/* Remark */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Remark
              {(status === 'Added in CRM' || status === 'Already Existing') && (
                <span className="ml-1.5 text-[10px] font-bold text-indigo-500 normal-case tracking-normal">— Required</span>
              )}
            </label>
            <textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder={
                status === 'Added in CRM'
                  ? 'Enter the CRM reference ID or details where this lead was added...'
                  : status === 'Already Existing'
                  ? 'Enter existing client details, reference ID, or how this lead is a duplicate...'
                  : 'Add a remark about this lead...'
              }
              rows={3}
              className={`w-full px-3 py-2.5 rounded-xl border text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 resize-none transition-all ${
                (status === 'Added in CRM' || status === 'Already Existing')
                  ? 'border-indigo-300 bg-indigo-50/30'
                  : 'border-slate-200'
              }`}
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50/50">
          <button
            onClick={onCancel}
            className="h-9 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(status, remark.trim(), calledDate, uid.trim())}
            className="h-9 px-5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-500/30 transition-all"
          >
            Update
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Main Dashboard ---
export default function AdminDashboard() {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [leads, setLeads] = useState<UnifiedLead[]>([]);
  const [stats, setStats] = useState<DashboardStats>({ totalLeads: 0, newToday: 0, callDone: 0, notResponding: 0, addedInCRM: 0, bySource: {}, byStatus: {} });
  const [filters, setFilters] = useState<LeadFilters>(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedLead, setSelectedLead] = useState<UnifiedLead | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [sourcePages, setSourcePages] = useState<Record<string, number>>({});
  const [statusBreakdown, setStatusBreakdown] = useState<Record<string, number>>({});
  const [statusChangeLead, setStatusChangeLead] = useState<UnifiedLead | null>(null);
  const [showUserMgmt, setShowUserMgmt] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showExportCustom, setShowExportCustom] = useState(false);
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<number>>(new Set());
  const [showFlaggedSection, setShowFlaggedSection] = useState(false);
  const [flaggedFilter, setFlaggedFilter] = useState<FlaggedSectionFilter>('All');
  const [maintenanceMode, setMaintenanceModeState] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [leadsData, statsData] = await Promise.all([
      fetchAllLeads(filters),
      fetchDashboardStats(),
    ]);
    // Sort newest first
    leadsData.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    setLeads(leadsData);
    setStats(statsData);
    setSourcePages(statsData.bySource || {});
    setStatusBreakdown(statsData.byStatus || {});
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    setAdmin(getCurrentAdmin());
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (admin?.role !== 'admin') return;
    fetchAdminSiteSettings().then((result) => {
      if (result.success && result.settings) {
        setMaintenanceModeState(result.settings.maintenance_mode);
      }
    });
  }, [admin?.role]);

  const handleMaintenanceToggle = async () => {
    if (maintenanceLoading || admin?.role !== 'admin') return;
    const next = !maintenanceMode;
    const label = next ? 'Turn ON maintenance mode? The public website will be unavailable.' : 'Turn OFF maintenance mode? The public website will go live again.';
    if (!window.confirm(label)) return;

    setMaintenanceLoading(true);
    const result = await setMaintenanceMode(next);
    setMaintenanceLoading(false);
    if (result.success && result.settings) {
      setMaintenanceModeState(result.settings.maintenance_mode);
    } else {
      window.alert(result.error || 'Failed to update maintenance mode.');
    }
  };

  useEffect(() => {
    const stopPolling = startPolling(() => loadData(), 10000);
    return () => stopPolling();
  }, [loadData]);

  const handleStatusChangeRequest = (lead: UnifiedLead, newStatus?: LeadStatus) => {
    if (newStatus) {
      // Quick select from dropdown — open modal pre-set to that status
      setStatusChangeLead({ ...lead, status: newStatus } as any);
      // We store temporary desired status using a trick: we'll read from modal
    }
    setStatusChangeLead(lead);
  };

  const handleStatusConfirm = async (status: LeadStatus, remark: string, calledDate: string, uid: string) => {
    if (!statusChangeLead) return;
    const success = await updateLeadStatus(statusChangeLead.id, status, remark || undefined, uid || undefined);
    if (success) {
      setLeads((prev) => prev.map((l) => (l.id === statusChangeLead.id ? {
        ...l,
        status,
        remark: remark || l.remark,
        uid: uid || l.uid,
        called_date: calledDate || l.called_date,
      } : l)));
      loadData(); // refresh stats
    }
    setStatusChangeLead(null);
  };

  // Clickable card filter
  const handleCardClick = (statusFilter: LeadStatus | 'All') => {
    setFilters({ ...filters, status: statusFilter });
    setPage(1);
  };

  const handleLogout = () => {
    signOut();
    window.location.reload();
  };

  // Main table = proper leads only. Flagged / spam / rejected / deleted go below.
  const viewingFlaggedStatus = FLAGGED_STATUSES.includes(filters.status as LeadStatus);
  const activeLeads = viewingFlaggedStatus
    ? leads.filter((l) => l.status === filters.status)
    : leads.filter((l) => !isFlaggedLead(l) && (filters.status === 'All' || l.status === filters.status));

  const allFlaggedLeads = leads.filter(isFlaggedLead);
  const flaggedLeads = allFlaggedLeads.filter((l) => {
    if (flaggedFilter === 'All') return true;
    if (flaggedFilter === 'Anti-spam') return !!l.spam_flags;
    return l.status === flaggedFilter;
  });

  const activeTotalPages = Math.ceil(activeLeads.length / ITEMS_PER_PAGE);
  const activePaginatedLeads = activeLeads.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  // Selection helpers
  const toggleLeadSelection = (id: number) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllOnPage = () => {
    const ids = activePaginatedLeads.map((l) => l.id);
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const deselectAll = () => setSelectedLeadIds(new Set());

  const allOnPageSelected = activePaginatedLeads.length > 0 && activePaginatedLeads.every((l) => selectedLeadIds.has(l.id));
  const someOnPageSelected = activePaginatedLeads.some((l) => selectedLeadIds.has(l.id));

  const handleDownloadSelected = () => {
    const selectedLeads = leads.filter((l) => selectedLeadIds.has(l.id));
    if (selectedLeads.length > 0) exportLeadsToCSV(selectedLeads);
  };

  const handleBulkStatusChange = async (newStatus: LeadStatus) => {
    const ids = Array.from(selectedLeadIds);
    let success = true;
    for (const id of ids) {
      const ok = await updateLeadStatus(id, newStatus);
      if (!ok) success = false;
    }
    if (success) {
      setSelectedLeadIds(new Set());
      loadData();
    }
  };

  const statCards = [
    { label: 'Total Leads', value: stats.totalLeads, icon: Users, color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200', hover: 'hover:border-indigo-400 hover:shadow-indigo-100', filter: 'All' as const },
    { label: 'New Today', value: stats.newToday, icon: UserPlus, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', hover: 'hover:border-blue-400 hover:shadow-blue-100', filter: 'New' as LeadStatus },
    { label: 'Call Done', value: stats.callDone, icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', hover: 'hover:border-green-400 hover:shadow-green-100', filter: 'Call Done' as LeadStatus },
    { label: 'Not Responding', value: stats.notResponding, icon: PhoneOff, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', hover: 'hover:border-orange-400 hover:shadow-orange-100', filter: 'Not Responding' as LeadStatus },
    { label: 'Added in CRM', value: stats.addedInCRM, icon: Database, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', hover: 'hover:border-emerald-400 hover:shadow-emerald-100', filter: 'Added in CRM' as LeadStatus },
  ];

  const activeCardFilter = filters.status;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-30">
        <div className="max-w-[1480px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Hola World Migration" className="h-9 w-auto" />
            <span className="text-xs text-slate-400 px-1.5 py-0.5 rounded-md bg-slate-100 font-medium hidden sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            {admin && (
              <div className="hidden md:flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center">
                  <span className="text-xs font-bold text-indigo-600">{(admin.name || admin.email)[0].toUpperCase()}</span>
                </div>
                <span className="text-sm font-medium text-slate-600">{admin.name || admin.email}</span>
              </div>
            )}
            {admin?.role === 'admin' && (
              <button
                onClick={handleMaintenanceToggle}
                disabled={maintenanceLoading}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                  maintenanceMode
                    ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 ring-1 ring-amber-300'
                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                } disabled:opacity-60`}
                title={maintenanceMode ? 'Maintenance mode is ON — click to turn off' : 'Turn on maintenance mode'}
              >
                <Construction className={`w-4 h-4 ${maintenanceLoading ? 'animate-pulse' : ''}`} />
                <span className="hidden sm:inline">Maintenance</span>
                <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                  maintenanceMode ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-600'
                }`}>
                  {maintenanceMode ? 'On' : 'Off'}
                </span>
              </button>
            )}
            {admin?.role === 'admin' && (
              <button
                onClick={() => setShowUserMgmt(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-500 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
                title="User Management"
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline">Users</span>
              </button>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1480px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Stats Cards — Clickable */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {statCards.map((card) => {
            const isActive = activeCardFilter === card.filter;
            return (
              <button
                key={card.label}
                onClick={() => handleCardClick(card.filter)}
                className={`bg-white rounded-2xl border p-4 sm:p-5 text-left transition-all duration-200 cursor-pointer group ${card.hover} ${
                  isActive ? `${card.border} shadow-md ring-1 ring-offset-0 ${card.border}` : 'border-slate-200/80 hover:shadow-md'
                }`}
              >
                <div className="flex items-center justify-between mb-2.5">
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${isActive ? card.color : 'text-slate-400'}`}>{card.label}</span>
                  <div className={`w-8 h-8 rounded-xl ${card.bg} flex items-center justify-center transition-transform group-hover:scale-110`}>
                    <card.icon className={`w-4 h-4 ${card.color}`} />
                  </div>
                </div>
                <p className={`text-2xl sm:text-3xl font-extrabold tracking-tight ${card.color}`}>{card.value}</p>
              </button>
            );
          })}
        </div>

        {/* Analytics Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Leads by Source */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Leads by Source</h3>
            <div className="space-y-3">
              {Object.entries(sourcePages).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([pg, count]) => (
                <div key={pg} className="flex items-center gap-3">
                  <span className="text-sm text-slate-600 truncate flex-1 min-w-0">{pg}</span>
                  <div className="w-28 h-2.5 bg-slate-100 rounded-full overflow-hidden flex-shrink-0">
                    <div
                      className="h-full bg-gradient-to-r from-indigo-500 to-indigo-400 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, (count / Math.max(...Object.values(sourcePages), 1)) * 100)}%` }}
                    />
                  </div>
                  <span className="text-sm font-bold text-slate-700 w-8 text-right tabular-nums">{count}</span>
                </div>
              ))}
              {Object.keys(sourcePages).length === 0 && (
                <p className="text-sm text-slate-400 py-2">No source data yet</p>
              )}
            </div>
          </div>

          {/* Leads by Status */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Leads by Status</h3>
            <div className="space-y-3">
              {LEAD_STATUSES.map((s) => {
                const count = statusBreakdown[s] || 0;
                const colors = STATUS_COLORS[s];
                const total = Object.values(statusBreakdown).reduce((a, b) => a + b, 0) || 1;
                return (
                  <div key={s} className="flex items-center gap-3">
                    <div className="flex items-center gap-2 w-28 flex-shrink-0">
                      <span className={`w-2.5 h-2.5 rounded-full ${colors.dot}`} />
                      <span className="text-sm text-slate-600">{s}</span>
                    </div>
                    <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${colors.dot.replace('bg-', 'bg-')}`}
                        style={{ width: `${Math.min(100, (count / total) * 100)}%` }}
                      />
                    </div>
                    <span className="text-sm font-bold text-slate-700 w-8 text-right tabular-nums">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-sm">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="relative flex-1 w-full sm:w-auto">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={filters.search}
                onChange={(e) => { setFilters({ ...filters, search: e.target.value }); setPage(1); }}
                autoComplete="off"
                name="lead-search-filter"
                className="w-full h-10 pl-10 pr-4 rounded-xl border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all"
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <button
                  onClick={() => setShowStatusMenu(!showStatusMenu)}
                  className={`h-10 px-3.5 pr-2.5 rounded-xl border text-sm font-medium flex items-center gap-2 transition-all min-w-[130px] ${
                    filters.status !== 'All'
                      ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {filters.status !== 'All' && (
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_COLORS[filters.status as LeadStatus]?.dot || 'bg-slate-300'}`} />
                  )}
                  <span className="flex-1 text-left truncate">{filters.status === 'All' ? 'All Status' : filters.status}</span>
                  <svg className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${showStatusMenu ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showStatusMenu && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setShowStatusMenu(false)} />
                    <div className="absolute left-0 top-full mt-2 z-40 bg-white rounded-xl border border-slate-200 shadow-xl w-48 overflow-hidden py-1.5">
                      <button
                        onClick={() => { setFilters({ ...filters, status: 'All' }); setPage(1); setShowStatusMenu(false); }}
                        className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm font-medium transition-colors ${
                          filters.status === 'All' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <span className="w-2 h-2 rounded-full bg-slate-300 flex-shrink-0" />
                        All Status
                        {filters.status === 'All' && <svg className="w-3.5 h-3.5 ml-auto text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                      </button>
                      <div className="my-1 border-t border-slate-100" />
                      <p className="px-3.5 pt-1 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Main leads</p>
                      {PRIMARY_STATUSES.map((s) => {
                        const colors = STATUS_COLORS[s];
                        const isActive = filters.status === s;
                        return (
                          <button
                            key={s}
                            onClick={() => { setFilters({ ...filters, status: s }); setPage(1); setShowStatusMenu(false); }}
                            className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm font-medium transition-colors ${
                              isActive ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'
                            }`}
                          >
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${colors.dot}`} />
                            {s}
                            {isActive && <svg className="w-3.5 h-3.5 ml-auto text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                          </button>
                        );
                      })}
                      <div className="my-1 border-t border-slate-100" />
                      <p className="px-3.5 pt-1 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Flagged & other</p>
                      {FLAGGED_STATUSES.map((s) => {
                        const colors = STATUS_COLORS[s];
                        const isActive = filters.status === s;
                        return (
                          <button
                            key={s}
                            onClick={() => { setFilters({ ...filters, status: s }); setPage(1); setShowStatusMenu(false); }}
                            className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm font-medium transition-colors ${
                              isActive ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'
                            }`}
                          >
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${colors.dot}`} />
                            {s}
                            {isActive && <svg className="w-3.5 h-3.5 ml-auto text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`h-10 px-3.5 rounded-xl border text-sm font-medium flex items-center gap-1.5 transition-all ${
                  showFilters ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Filter className="w-3.5 h-3.5" />
                Filters
              </button>

              <button
                onClick={loadData}
                disabled={loading}
                className="h-10 w-10 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center justify-center disabled:opacity-50 transition-colors"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  className="h-10 px-3.5 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-1.5 transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Export</span>
                </button>

                {showExportMenu && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => { setShowExportMenu(false); setShowExportCustom(false); }} />
                    <div className="absolute right-0 top-full mt-2 z-40 bg-white rounded-xl border border-slate-200 shadow-xl w-72 overflow-hidden">
                      {!showExportCustom ? (
                        <div className="p-2 space-y-1">
                          <button
                            onClick={() => {
                              exportLeadsFromServer();
                              setShowExportMenu(false);
                            }}
                            className="w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors flex items-center gap-2"
                          >
                            <Download className="w-4 h-4" />
                            Export All Leads
                          </button>
                          <button
                            onClick={() => setShowExportCustom(true)}
                            className="w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors flex items-center gap-2"
                          >
                            <CalendarDays className="w-4 h-4" />
                            Custom Date Range
                          </button>
                        </div>
                      ) : (
                        <div className="p-4 space-y-3">
                          <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Select Date Range</h4>
                          <div className="space-y-2">
                            <div>
                              <label className="block text-[11px] font-semibold text-slate-500 mb-1">From</label>
                              <input
                                type="date"
                                value={exportFrom}
                                onChange={(e) => setExportFrom(e.target.value)}
                                onClick={(e) => { try { (e.currentTarget as any).showPicker(); } catch {} }}
                                autoComplete="off"
                                data-form-type="other"
                                className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-700 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 bg-white cursor-pointer"
                              />
                            </div>
                            <div>
                              <label className="block text-[11px] font-semibold text-slate-500 mb-1">To</label>
                              <input
                                type="date"
                                value={exportTo}
                                onChange={(e) => setExportTo(e.target.value)}
                                onClick={(e) => { try { (e.currentTarget as any).showPicker(); } catch {} }}
                                autoComplete="off"
                                data-form-type="other"
                                className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-700 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 bg-white cursor-pointer"
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-2 pt-1">
                            <button
                              onClick={() => {
                                exportLeadsFromServer(exportFrom || undefined, exportTo || undefined);
                                setShowExportMenu(false);
                                setShowExportCustom(false);
                                setExportFrom('');
                                setExportTo('');
                              }}
                              disabled={!exportFrom && !exportTo}
                              className="flex-1 h-9 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-1.5"
                            >
                              <Download className="w-3.5 h-3.5" /> Export
                            </button>
                            <button
                              onClick={() => { setShowExportCustom(false); setExportFrom(''); setExportTo(''); }}
                              className="h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-500 hover:bg-slate-50 transition-colors"
                            >
                              Back
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Source Page</label>
                  <select
                    value={filters.sourcePage}
                    onChange={(e) => { setFilters({ ...filters, sourcePage: e.target.value }); setPage(1); }}
                    className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm text-slate-700 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 bg-white transition-all"
                  >
                    <option value="">All Pages</option>
                    {Object.keys(sourcePages).map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">From Date</label>
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => { setFilters({ ...filters, dateFrom: e.target.value }); setPage(1); }}
                    onClick={(e) => { try { (e.currentTarget as any).showPicker(); } catch {} }}
                    autoComplete="off"
                    data-form-type="other"
                    className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm text-slate-700 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 bg-white transition-all cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">To Date</label>
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => { setFilters({ ...filters, dateTo: e.target.value }); setPage(1); }}
                    onClick={(e) => { try { (e.currentTarget as any).showPicker(); } catch {} }}
                    autoComplete="off"
                    data-form-type="other"
                    className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm text-slate-700 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 bg-white transition-all cursor-pointer"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => { setFilters(DEFAULT_FILTERS); setPage(1); }}
                    className="h-10 px-4 w-full rounded-xl border border-red-200 text-sm font-medium text-red-500 hover:bg-red-50 hover:text-red-600 flex items-center justify-center gap-1.5 transition-all"
                  >
                    <X className="w-3.5 h-3.5" /> Clear All
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bulk Actions Bar */}
        {selectedLeadIds.size > 0 && (
          <div className="bg-indigo-50 rounded-2xl border border-indigo-200 px-5 py-3 flex items-center justify-between">
            <span className="text-sm font-semibold text-indigo-700">
              {selectedLeadIds.size} lead{selectedLeadIds.size > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDownloadSelected}
                className="h-9 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 flex items-center gap-1.5 transition-all"
              >
                <Download className="w-3.5 h-3.5" /> Download Selected
              </button>
              {admin?.role === 'admin' ? (
                <button
                  onClick={() => handleBulkStatusChange('Deleted')}
                  className="h-9 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 flex items-center gap-1.5 transition-all"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete Selected
                </button>
              ) : (
                <button
                  onClick={() => handleBulkStatusChange('Rejected')}
                  className="h-9 px-4 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 flex items-center gap-1.5 transition-all"
                >
                  <ShieldX className="w-3.5 h-3.5" /> Reject Selected
                </button>
              )}
              <button
                onClick={deselectAll}
                className="h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-500 hover:bg-slate-50 transition-colors"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Lead Table — proper / main leads only */}
        <div className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden shadow-sm">
          <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between bg-slate-50/40">
            <div>
              <h3 className="text-sm font-bold text-slate-900">
                {viewingFlaggedStatus ? filters.status : 'Main Leads'}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {viewingFlaggedStatus
                  ? `Showing ${filters.status} records only`
                  : 'New, Call Done, Not Responding, Added in CRM, Already Existing'}
              </p>
            </div>
            <span className="text-xs font-semibold text-slate-500 tabular-nums">
              {activeLeads.length} record{activeLeads.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200/60">
                  <th className="px-4 py-3.5 w-10">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      ref={(el) => { if (el) el.indeterminate = someOnPageSelected && !allOnPageSelected; }}
                      onChange={() => allOnPageSelected ? deselectAll() : selectAllOnPage()}
                      className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    />
                  </th>
                  <th className="px-4 py-3.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap min-w-[100px]">Date</th>
                  <th className="px-4 py-3.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider min-w-[120px]">Name</th>
                  <th className="px-4 py-3.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden md:table-cell">Phone</th>
                  <th className="px-4 py-3.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden md:table-cell">Dial Code</th>
                  <th className="px-4 py-3.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Email</th>
                  <th className="px-4 py-3.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden xl:table-cell">Message</th>
                  <th className="px-4 py-3.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Score</th>
                  <th className="px-4 py-3.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Remark</th>
                  <th className="px-4 py-3.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && leads.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-4 py-16 text-center text-sm text-slate-400">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-3 text-indigo-400" />
                      Loading leads...
                    </td>
                  </tr>
                ) : activePaginatedLeads.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-4 py-16 text-center">
                      <div className="text-slate-300 mb-2">
                        <Users className="w-10 h-10 mx-auto" />
                      </div>
                      <p className="text-sm font-medium text-slate-400">No leads found</p>
                      <p className="text-xs text-slate-300 mt-1">Try adjusting your filters</p>
                    </td>
                  </tr>
                ) : (
                  activePaginatedLeads.map((lead) => (
                    <LeadRow
                      key={`${lead.lead_type}-${lead.id}`}
                      lead={lead}
                      onStatusChange={(l) => setStatusChangeLead(l)}
                      onViewDetails={() => setSelectedLead(lead)}
                      isSelected={selectedLeadIds.has(lead.id)}
                      onToggleSelect={toggleLeadSelection}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {activeTotalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3.5 border-t border-slate-100 bg-slate-50/40">
              <p className="text-xs font-medium text-slate-500">
                Showing <span className="text-slate-700">{(page - 1) * ITEMS_PER_PAGE + 1}–{Math.min(page * ITEMS_PER_PAGE, activeLeads.length)}</span> of <span className="text-slate-700">{activeLeads.length}</span> leads
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className="px-2 py-1 rounded-lg text-xs font-medium text-slate-500 hover:bg-white hover:shadow-sm disabled:opacity-30 transition-all"
                >
                  First
                </button>
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded-lg text-slate-500 hover:bg-white hover:shadow-sm disabled:opacity-30 transition-all"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {Array.from({ length: Math.min(5, activeTotalPages) }, (_, i) => {
                  let pageNum: number;
                  if (activeTotalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= activeTotalPages - 2) {
                    pageNum = activeTotalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all ${
                        page === pageNum
                          ? 'bg-indigo-600 text-white shadow-sm'
                          : 'text-slate-500 hover:bg-white hover:shadow-sm'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage(Math.min(activeTotalPages, page + 1))}
                  disabled={page === activeTotalPages}
                  className="p-1.5 rounded-lg text-slate-500 hover:bg-white hover:shadow-sm disabled:opacity-30 transition-all"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage(activeTotalPages)}
                  disabled={page === activeTotalPages}
                  className="px-2 py-1 rounded-lg text-xs font-medium text-slate-500 hover:bg-white hover:shadow-sm disabled:opacity-30 transition-all"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Flagged & Other — Spam, Not Eligible, Rejected, Deleted, anti-spam flags */}
        {!viewingFlaggedStatus && allFlaggedLeads.length > 0 && (
          <div className="bg-white rounded-2xl border border-amber-200/80 overflow-hidden shadow-sm">
            <button
              onClick={() => setShowFlaggedSection(!showFlaggedSection)}
              className="w-full px-5 py-4 flex items-center justify-between hover:bg-amber-50/40 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center">
                  <Flag className="w-4 h-4 text-amber-600" />
                </div>
                <div className="text-left">
                  <h3 className="text-sm font-bold text-slate-900">Flagged & Other</h3>
                  <p className="text-xs text-slate-500">
                    {allFlaggedLeads.length} lead{allFlaggedLeads.length !== 1 ? 's' : ''} — Spam, Not Eligible, Rejected, Deleted, anti-spam flags
                  </p>
                </div>
              </div>
              {showFlaggedSection ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>

            {showFlaggedSection && (
              <div className="border-t border-amber-100">
                <div className="px-4 py-3 flex flex-wrap gap-2 bg-amber-50/30">
                  {([
                    { key: 'All' as const, label: 'All', count: allFlaggedLeads.length },
                    { key: 'Spam' as const, label: 'Spam', count: allFlaggedLeads.filter((l) => l.status === 'Spam').length },
                    { key: 'Not Eligible' as const, label: 'Not Eligible', count: allFlaggedLeads.filter((l) => l.status === 'Not Eligible').length },
                    { key: 'Rejected' as const, label: 'Rejected', count: allFlaggedLeads.filter((l) => l.status === 'Rejected').length },
                    { key: 'Deleted' as const, label: 'Deleted', count: allFlaggedLeads.filter((l) => l.status === 'Deleted').length },
                    { key: 'Anti-spam' as const, label: 'Anti-spam flags', count: allFlaggedLeads.filter((l) => !!l.spam_flags).length },
                  ] as const).filter((t) => t.key === 'All' || t.count > 0).map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setFlaggedFilter(tab.key)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                        flaggedFilter === tab.key
                          ? 'bg-amber-100 text-amber-900 border-amber-300'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {tab.label}
                      <span className="ml-1.5 text-[10px] opacity-70">{tab.count}</span>
                    </button>
                  ))}
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-amber-50/50 border-b border-amber-100">
                        <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Date</th>
                        <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Name</th>
                        <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden md:table-cell">Phone</th>
                        <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Email</th>
                        <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Type</th>
                        <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-50">
                      {flaggedLeads.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-400">
                            <AlertTriangle className="w-5 h-5 mx-auto mb-2 text-amber-400" />
                            No leads in this flagged filter
                          </td>
                        </tr>
                      ) : (
                        flaggedLeads.map((lead) => (
                          <LeadRow
                            key={`flagged-${lead.id}`}
                            lead={lead}
                            onStatusChange={(l) => setStatusChangeLead(l)}
                            onViewDetails={() => setSelectedLead(lead)}
                          />
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Status Change Modal */}
      {statusChangeLead && (
        <StatusChangeModal
          lead={statusChangeLead}
          onConfirm={handleStatusConfirm}
          onCancel={() => setStatusChangeLead(null)}
        />
      )}

      {/* Lead Detail Modal */}
      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          adminId={admin?.id}
          onClose={() => setSelectedLead(null)}
          onStatusChange={(newStatus) => {
            setStatusChangeLead(selectedLead);
            setSelectedLead(null);
          }}
        />
      )}

      {/* User Management Modal */}
      {showUserMgmt && (
        <UserManagement currentAdminId={admin?.id} onClose={() => setShowUserMgmt(false)} />
      )}
    </div>
  );
}
